import React, { createContext, useState, useContext, useEffect } from 'react';
import { api } from '@/api/client';
import { clearPrivateChatBrowserState } from '@/lib/chat/privateChatStorage';

const AuthContext = createContext(/** @type {any} */ (undefined));

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [appPublicSettings, setAppPublicSettings] = useState(null);

  useEffect(() => {
    checkAuthState();

    // Listen for 401 events from the API interceptor (no hard reload needed)
    const handleForceLogout = () => {
      clearPrivateChatBrowserState();
      setUser(null);
      setIsAuthenticated(false);
      setIsLoadingAuth(false);
    };
    window.addEventListener('auth-logout', handleForceLogout);
    return () => window.removeEventListener('auth-logout', handleForceLogout);
  }, []);

  const checkAuthState = async () => {
    const token = localStorage.getItem('jwt_token');

    // No token — not an error, just not logged in
    if (!token) {
      setIsLoadingAuth(false);
      setIsAuthenticated(false);
      return;
    }

    // Token exists — validate against backend
    try {
      setIsLoadingAuth(true);
      const response = await api.auth.getUser();
      setUser(response.data.user);
      setIsAuthenticated(true);
      setIsLoadingAuth(false);
    } catch (error) {
      console.error('Auth check failed:', error);
      clearPrivateChatBrowserState();
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
      setIsLoadingAuth(false);
    }
  };

  const logout = () => {
    clearPrivateChatBrowserState();
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const navigateToLogin = () => {
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoadingAuth,
      isLoadingPublicSettings,
      authError,
      appPublicSettings,
      setUser,
      setIsAuthenticated,
      logout,
      navigateToLogin,
      checkAppState: checkAuthState
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
